import { useCallback, useEffect, useMemo, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import { useSelector } from 'react-redux';
import {
  OrdersAutoCancelConfigurationByMethod,
  OrdersAutoCancelPaymentMethod,
} from '@tiendanube/common';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { useAppDispatch } from 'App/store';
import { useAsyncFunc, useToast } from 'commons/hooks';
import {
  getOrdersAutoCancelConfiguration,
  getOrdersAutoCancelConfigurationData,
  getOrdersAutoCancelConfigurationStatus,
  saveOrdersAutoCancelConfiguration,
} from 'domains/Orders/Orders/ordersSlice';
import { trackingOrdersAutoCancelSave } from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

function useOrdersAutoCancelConfiguration() {
  const dispatch = useAppDispatch();
  const fetchedConfiguration = useSelector(
    getOrdersAutoCancelConfigurationData,
  );
  const { isLoading, isError } = useSelector(
    getOrdersAutoCancelConfigurationStatus,
  );
  const [isSaving, setIsSaving] = useState(false);
  const { closeToastProgress } = useToastProgress();
  const { addToast } = useToast();
  const t = useTranslationOrders('autoCancel');

  const [configuration, setConfiguration] =
    useState<OrdersAutoCancelConfigurationByMethod | null>(
      fetchedConfiguration,
    );

  // Used to compare with the current configuration to determine if there are unsaved changes.
  const [lastSavedConfiguration, setLastSavedConfiguration] =
    useState<OrdersAutoCancelConfigurationByMethod | null>(configuration);

  const hasConfigChanged = useMemo(
    () => !isEqual(lastSavedConfiguration, configuration),
    [lastSavedConfiguration, configuration],
  );

  const getConfiguration = useCallback(async () => {
    await dispatch(getOrdersAutoCancelConfiguration());
  }, [dispatch]);

  const onError = () => {
    closeToastProgress();
    addToast({
      label: t('toast.error'),
      appearance: 'danger',
    });
  };

  const buildTrackingEvent = (
    configuration: OrdersAutoCancelConfigurationByMethod,
  ): Record<string, string> => {
    const trackingObject = {
      update_stock: configuration.updateStock.toString(),
      notify_client: configuration.notifyClient.toString(),
    };

    // Add payment method configurations
    Object.entries(configuration.configuration).forEach(
      ([paymentMethod, methodConfig]) => {
        trackingObject[paymentMethod] = methodConfig.isActive.toString();
      },
    );

    return trackingObject;
  };

  const onSuccess = () => {
    closeToastProgress();
    setLastSavedConfiguration(configuration);

    // This should never happen but the ide complains.
    if (configuration) {
      trackingOrdersAutoCancelSave(buildTrackingEvent(configuration));
    }

    addToast({
      label: t('toast.success'),
      appearance: 'success',
    });
  };

  const [onSubmit] = useAsyncFunc<OrdersAutoCancelConfigurationByMethod, void>(
    async (configuration?: OrdersAutoCancelConfigurationByMethod) => {
      if (!configuration) {
        throw new Error('Orders auto cancel configuration can not be null');
      }
      try {
        setIsSaving(true);
        await saveConfiguration(configuration);
      } finally {
        setIsSaving(false);
      }
    },
    () => onSuccess(),
    () => onError(),
  );

  const saveConfiguration = useCallback(
    async (configuration: OrdersAutoCancelConfigurationByMethod) => {
      unwrapResult(
        await dispatch(saveOrdersAutoCancelConfiguration({ configuration })),
      );
    },
    [dispatch],
  );

  const validateConfiguration = (
    configuration: OrdersAutoCancelConfigurationByMethod,
  ): OrdersAutoCancelPaymentMethod[] => {
    const emptyMethods: OrdersAutoCancelPaymentMethod[] = [];

    Object.entries(configuration.configuration).forEach(
      ([paymentMethod, methodConfig]) => {
        if (methodConfig.isActive && methodConfig.expirationTime === null) {
          emptyMethods.push(paymentMethod as OrdersAutoCancelPaymentMethod);
        }
      },
    );

    return emptyMethods;
  };

  const isAnyPaymentMethodActive = useCallback(
    (configuration?: OrdersAutoCancelConfigurationByMethod): boolean => {
      if (!configuration) {
        return false;
      }
      return Object.values(configuration.configuration).some(
        (config) => config.isActive && !!config.expirationTime,
      );
    },
    [],
  );

  useEffect(() => {
    dispatch(getOrdersAutoCancelConfiguration());
  }, [dispatch]);

  useEffect(() => {
    setLastSavedConfiguration(fetchedConfiguration);
  }, [fetchedConfiguration]);

  return {
    fetchedConfiguration,
    configuration,
    setConfiguration,
    hasConfigChanged,
    isLoading,
    isError,
    isSaving,
    onSubmit,
    getConfiguration,
    validateConfiguration,
    isAnyPaymentMethodActive,
  };
}

export default useOrdersAutoCancelConfiguration;
